/**
 * Email Scenarios Based on 2025 Phishing Threat Intelligence
 * 
 * These scenarios are designed based on the latest 2025 phishing threat landscape as documented by:
 * - Kaspersky Security Network (2025): "Phishing and scams: how fraudsters are deceiving users in 2025"
 *   https://securelist.com/new-phishing-and-scam-trends-in-2025/117217/
 * - Hoxhunt (2025): "Phishing Trends Report (Updated for 2025)" 
 *   https://hoxhunt.com/guide/phishing-trends-report
 * - Microsoft Security Blog (2025): "Defending against evolving identity attack techniques"
 *   https://www.microsoft.com/en-us/security/blog/2025/05/29/defending-against-evolving-identity-attack-techniques/
 * 
 * Key 2025 Trends Incorporated:
 * 1. AI-Enhanced Personalization: Attackers use AI to analyze social media and corporate data 
 *    for highly targeted spear phishing (implemented in executive impersonation scenarios)
 * 2. Business Email Compromise (BEC): Focus on financial/invoice fraud targeting workplace processes
 *    rather than consumer-focused scams (implemented in invoice authorization scenarios) 
 * 3. Executive Impersonation: Sophisticated social engineering using authority figures and 
 *    confidential business contexts (implemented in CEO assistant scenarios)
 * 4. Multi-Platform & Multi-Stage: Complex attacks that bypass MFA through credential harvesting
 *    on legitimate-looking portals (implemented in login-required scenarios)
 * 5. Abuse of Trusted Platforms: Using legitimate services to bypass security filters
 *    (implemented through realistic domain spoofing)
 * 
 * Congruence Classification (for Fuzzy Trace Theory analysis):
 * - Congruent: Gist cues align with verbatim cues (looks phishing + is phishing, or looks legitimate + is legitimate)  
 * - Incongruent: Gist cues conflict with verbatim cues (looks legitimate + is phishing, or looks phishing + is legitimate)
 */

// Typed verbatim cue keys to prevent typos and ensure rubric alignment
export type VerbatimKey =
  | 'dmarcAligned' | 'dkimPass' | 'exactOrgDomain' | 'firstPartySubdomain'
  | 'linkPreviewMatchesText' | 'domainMismatch' | 'lookalikeDomain'
  | 'credentialHarvest' | 'externalPaymentLink' | 'policyReference'
  | 'validContactInfo' | 'specificTimestamps' | 'firstPartyLinks'
  | 'httpNotHttps' | 'certWarning' | 'contextualDetails'
  | 'unverifiableUrgency' | 'obviousFakeDomain' | 'advanceFeeRequest'
  | 'unrealisticOffer' | 'senderDomainMatchesClaimedOrganization'
  | 'linkDomainMatchesClaimedService';

// Invariant helpers for production readiness
export function assertEight(items: unknown[], label: string) {
  if (items.length !== 8) {
    throw new Error(`Expected 8 items for ${label}, got ${items.length}`);
  }
}

export interface EmailScenario {
  id: string;
  domain: string; // Dom1-Dom8: Counterbalancing domain for experimental design
  sender: string;
  senderDisplay?: string;
  senderAddress?: string;
  replyTo?: string;
  subject: string;
  preview: string;
  fullContent: string;
  time: string;
  isPhishing: boolean;
  hasLink: boolean;
  hasAttachment?: boolean;
  attachmentName?: string;
  unread: boolean;
  decision?: 'engage' | 'verify' | 'ignore' | 'report' | null;

  // Congruence classification
  congruence: 'congruent' | 'incongruent';

  // Email category for FTT microsurvey contextualization
  emailCategory?: 'system-notice' | 'colleague-work' | 'authority-request' | 'financial-transaction' | 'policy-compliance' | 'security-alert' | 'payroll-document';

  // Cues for FTT analysis
  gistCues: string[];
  verbatimCues: VerbatimKey[];

  // Additional metadata
  threatIndicators?: string[];
  legitimacyIndicators?: string[];
  securityWarnings?: string[];
  conflictLevel: 'low' | 'medium' | 'high';
  complexity: number;

  // SME coding fields for objective cue clarity assessment
  cueClarity?: 'clear' | 'ambiguous';
  smeScores?: {
    sender: 0 | 1 | 2 | 3;
    link: 0 | 1 | 2 | 3;
    domain: 0 | 1 | 2 | 3;
    cert: 0 | 1 | 2 | 3;
    content: 0 | 1 | 2 | 3;
  };
}

// First-party domain allowlist for automated truth checks
export const firstPartyDomains = [
  'company.com',
  'sharepoint.company.com', 
  'training.company.com',
  'id.company.com',
  'intranet.company.com',
  'benefits.company.com',
  'hr.company.com'
];

export const emailScenarios: EmailScenario[] = [
  // CONGRUENT LEGITIMATE EMAILS (looks legitimate + is legitimate)
  {
    id: 'e1',
    domain: 'Dom1', // IT Security & System Alerts
    sender: 'IT Support <support@company.com>',
    senderDisplay: 'IT Support',
    senderAddress: 'support@company.com',
    subject: 'Scheduled System Maintenance',
    preview: 'Our systems will undergo maintenance this weekend from 2 AM to 6 AM. Please review details.',
    fullContent: `<div class="email-body">
      <p>Dear Team,</p>
      <p>This is to inform you that our systems will undergo scheduled maintenance this weekend:</p>
      <ul>
        <li><strong>Date:</strong> Saturday, November 15, 2025</li>
        <li><strong>Time:</strong> 2:00 AM - 6:00 AM EST</li>
        <li><strong>Impact:</strong> Email and internal systems will be unavailable</li>
      </ul>
      <p>For more information about affected systems, <span class="email-link" data-url="https://intranet.company.com/it/maintenance">view the maintenance schedule</span>.</p>
      <p>No action is required from your end. We apologize for any inconvenience.</p>
      <p>Best regards,<br/>IT Support Team</p>
    </div>`,
    time: '10:32 AM',
    isPhishing: false,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'congruent',
    emailCategory: 'system-notice',
    legitimacyIndicators: ['internalDomain', 'intranetLink', 'professionalTone'],
    gistCues: ['Professional format', 'IT Support sender', 'Scheduled maintenance'],
    verbatimCues: ['exactOrgDomain', 'firstPartyLinks', 'specificTimestamps'],
    conflictLevel: 'low',
    complexity: 0.2
  },

  {
    id: 'e2',
    domain: 'Dom2', // Colleague Collaboration
    sender: 'Sarah Johnson <sjohnson@company.com>',
    subject: 'Meeting Notes from Yesterday',
    preview: 'Hi team, attached are the notes from our project meeting. Please review the action items...',
    fullContent: `<div class="email-body">
      <p>Hi team,</p>
      <p>Attached are the notes from yesterday's project meeting. Please review the action items assigned to you.</p>
      <p>Key decisions:</p>
      <ul>
        <li>Timeline moved up by 2 weeks</li>
        <li>Budget approved for additional resources</li>
        <li>Weekly check-ins moved to Thursdays</li>
      </ul>
      <p>The recording has been uploaded to our SharePoint folder. Please complete your assigned tasks by Friday and update your progress in the project tracker.</p>
      <p>Let me know if you have any questions!</p>
      <p>Best,<br/>Sarah</p>
    </div>`,
    time: '9:45 AM',
    isPhishing: false,
    hasLink: false,
    hasAttachment: true,
    attachmentName: 'Project_Meeting_Notes.docx',
    unread: true,
    decision: null,
    congruence: 'congruent',
    emailCategory: 'colleague-work',
    legitimacyIndicators: ['knownSender', 'contextuallyRelevant', 'internalDomain', 'attachmentProvided'],
    gistCues: ['Colleague name', 'Meeting context', 'Professional tone'],
    verbatimCues: ['exactOrgDomain', 'contextualDetails'],
    conflictLevel: 'low',
    complexity: 0.3
  },

  // INCONGRUENT PHISHING EMAILS (looks legitimate but is phishing)
  {
    id: 'e3',
    domain: 'Dom4', // Executive/Leadership Requests
    sender: 'CEO Office <exec.assistant@company.com>',
    subject: 'Confidential: Board Meeting Preparation - Q1 Budget Review',
    preview: 'Need your input on Q1 budget projections before board presentation. Please access the analysis...',
    fullContent: `<div class="email-body">
      <p>Hi there,</p>
      <p>Hope your week is going well. James (CEO) asked me to reach out regarding the upcoming board meeting next Tuesday.</p>
      <p>We're finalizing Q1 budget projections and need department input before the presentation. Could you access the analysis here and provide your thoughts?</p>
      <p>Specifically looking for feedback on:</p>
      <ul>
        <li>Headcount planning for Q1 hiring</li>
        <li>Technology infrastructure investments</li>
        <li>Marketing spend allocation across channels</li>
      </ul>
      <p>I know this is short notice, but the board moved the meeting up due to investor calls.</p>
      <p><span class="email-link" data-url="https://files.company-support.net/q1-budget-analysis">Access the budget analysis here</span> (requires login)</p>
      <p>If you can get your feedback to me by Thursday morning, that would be perfect.</p>
      <p>Thanks so much!<br/>Rachel Martinez<br/>Executive Assistant to CEO<br/>Direct: (555) 123-4567</p>
      <small>Sent from iPhone - please excuse any typos</small>
    </div>`,
    time: '11:15 AM',
    isPhishing: true,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'incongruent',
    emailCategory: 'authority-request',
    threatIndicators: ['executiveImpersonation', 'credentialHarvesting', 'domainSpoofing', 'socialEngineering'],
    gistCues: ['CEO authority', 'Confidential meeting', 'Professional tone'],
    verbatimCues: ['domainMismatch', 'credentialHarvest', 'unverifiableUrgency'],
    conflictLevel: 'high',
    complexity: 0.95
  },

  {
    id: 'e4',
    domain: 'Dom8', // External Payments (Vendor/Supplier)
    sender: 'Finance Team <finance@company-invoicing.com>',
    subject: 'Invoice Payment Authorization Required - Vendor #V2025-4738',
    preview: 'Urgent payment approval needed for Q4 consulting services. Please review and authorize...',
    fullContent: `<div class="email-body">
      <p>Hello,</p>
      <p>We received an invoice from our consulting partner that requires your approval for payment processing:</p>
      <p><strong>Invoice Details:</strong></p>
      <ul>
        <li>Vendor: TechAdvice Consulting LLC</li>
        <li>Amount: <strong>$24,750.00</strong></li>
        <li>Description: Q1 Digital Transformation Strategy</li>
        <li>Due Date: March 31, 2025</li>
        <li>Reference: INV-2025-Q1-DTS</li>
      </ul>
      <p>This payment is time-sensitive to maintain our vendor relationship and project timeline.</p>
      <p><span class="email-link" data-url="https://approve.accounting-company.com/auth/INV2025Q1">Click here to review and authorize payment</span></p>
      <p>Please complete authorization by end of business today.</p>
      <p>For questions about this invoice, contact our accounts team at: accounting@company.com or (555) 123-4500</p>
      <p>Best regards,<br/>Sarah Chen<br/>Finance Operations Team</p>
      <small>Company Finance Dept. | Invoice Management System</small>
    </div>`,
    time: '8:20 AM',
    isPhishing: true,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'incongruent',
    emailCategory: 'financial-transaction',
    threatIndicators: ['businessEmailCompromise', 'invoiceFraud', 'domainSpoofing', 'urgencyManipulation'],
    gistCues: ['Professional finance request', 'Legitimate invoice format', 'Detailed vendor info'],
    verbatimCues: ['domainMismatch', 'externalPaymentLink', 'validContactInfo'],
    conflictLevel: 'high',
    complexity: 0.9
  },

  // INCONGRUENT LEGITIMATE EMAILS (looks suspicious but is legitimate)
  {
    id: 'e5',
    domain: 'Dom6', // Training & Compliance
    sender: 'noreply@training.company.com',
    subject: 'URGENT: Complete Security Training by Friday',
    preview: 'MANDATORY: All employees must complete security training. Failure to comply will result in account suspension...',
    fullContent: `<div class="email-body">
      <p><strong>MANDATORY SECURITY TRAINING</strong></p>
      <p>Dear Employee,</p>
      <p>As per company policy, all employees MUST complete annual security awareness training.</p>
      <p><strong>DEADLINE: This Friday at 5:00 PM</strong></p>
      <p><strong>Failure to complete will result in temporary account suspension until training is completed.</strong></p>
      <p><span class="email-link" data-url="https://training.company.com/security2025">Click here to access the training portal</span></p>
      <p>This training takes approximately 30 minutes to complete.</p>
      <p>For questions, contact HR at x5555 or hr@company.com</p>
      <p>Thank you for your cooperation,<br/>Human Resources</p>
      <small>Sent via Company Training System (training.company.com)</small>
    </div>`,
    time: 'Yesterday',
    isPhishing: false,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'incongruent',
    emailCategory: 'policy-compliance',
    legitimacyIndicators: ['legitimateTraining', 'internalContact', 'properProcedure'],
    gistCues: ['Urgent tone', 'Threat of suspension', 'Mandatory deadline'],
    verbatimCues: ['firstPartySubdomain', 'validContactInfo', 'policyReference'],
    conflictLevel: 'high',
    complexity: 0.8
  },

  {
    id: 'e6',
    domain: 'Dom1', // IT Security & System Alerts
    sender: 'automated-system@id.company.com',
    subject: 'ACTION REQUIRED: Verify Your Identity',
    preview: 'Your account flagged for unusual activity. Immediate verification required to prevent lockout...',
    fullContent: `<div class="email-body">
      <p>Our automated security system has flagged your account for unusual activity.</p>
      <p><strong>What happened:</strong> Multiple failed login attempts detected from unfamiliar location.</p>
      <p><strong>Action Required:</strong> Verify your identity within 48 hours</p>
      <p><strong>If you do not verify, your account will be locked for security reasons.</strong></p>
      <p><span class="email-link" data-url="https://id.company.com/verify">Click here to verify your identity</span></p>
      <p>This is a legitimate security measure required by our IT policy (Policy #SEC-2025-03).</p>
      <p>If you have questions, contact IT Support at:<br/>
      Phone: (555) 123-4567<br/>
      Email: itsupport@company.com</p>
      <p>Company IT Security Team</p>
    </div>`,
    time: '3:15 PM',
    isPhishing: false,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'incongruent',
    emailCategory: 'security-alert',
    legitimacyIndicators: ['legitimateSecurityProcess', 'properContacts', 'policyReference'],
    gistCues: ['Warning symbols', 'Account lockout threat', 'Automated sender'],
    verbatimCues: ['firstPartySubdomain', 'validContactInfo', 'policyReference'],
    conflictLevel: 'high',
    complexity: 0.9
  },

  // CONGRUENT PHISHING EMAILS (looks suspicious and is phishing)
  {
    id: 'e7',
    domain: 'Dom1', // IT Security & System Alerts
    sender: 'IT-Security-Team@company-it.org',
    subject: 'Password Policy Update - Action Required by Friday',
    preview: 'New company password requirements effective next week. Update your credentials in our portal...',
    fullContent: `<div class="email-body">
      <p>Dear Employee,</p>
      <p>As part of our ongoing security improvements, we're implementing updated password requirements effective Monday, December 2nd.</p>
      <p><strong>New Requirements:</strong></p>
      <ul>
        <li>Minimum 12 characters with special symbols</li>
        <li>No reuse of last 6 passwords</li>
        <li>Mandatory update every 90 days</li>
      </ul>
      <p>To avoid account lockout, please update your password before the deadline:</p>
      <p><span class="email-link" data-url="https://password-update.company-it.org/portal">Access Password Update Portal</span></p>
      <p>If you don't update by Friday 5 PM, your account will be temporarily restricted until you comply.</p>
      <p>Questions? Contact IT Support at (555) 123-4567.</p>
      <p>Best regards,<br/>Company IT Security Team</p>
      <small>IT Security Notice - Company-IT.org</small>
    </div>`,
    time: '12:45 PM',
    isPhishing: true,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'congruent',
    emailCategory: 'security-alert',
    threatIndicators: ['domainMismatch', 'credentialHarvest', 'policyDeadline'],
    gistCues: ['IT policy change', 'Password deadline', 'Account restriction threat'],
    verbatimCues: ['domainMismatch', 'credentialHarvest', 'unverifiableUrgency'],
    conflictLevel: 'medium',
    complexity: 0.6
  },

  {
    id: 'e8',
    domain: 'Dom5', // HR & Payroll
    sender: 'payroll-notifications@company-hr.net',
    subject: 'W-2 Tax Form Download - Deadline January 31st',
    preview: 'Your 2024 W-2 form is ready for download. Please verify your information and download by month-end...',
    fullContent: `<div class="email-body">
      <p>Dear Employee,</p>
      <p>Your 2024 W-2 tax documentation is now available in our secure portal.</p>
      <p>To ensure timely tax filing, please download your forms before January 31st.</p>
      <p><strong>What You Need:</strong></p>
      <ul>
        <li>Your employee ID number</li>
        <li>Last 4 digits of your Social Security Number</li>
        <li>Current home address for verification</li>
      </ul>
      <p><span class="email-link" data-url="https://w2-forms.company-hr.net/employee-portal">Access W-2 Download Portal</span></p>
      <p>If you experience any technical issues, please contact Payroll at payroll@company-hr.net or (555) 123-4590.</p>
      <p>Important: This link expires on January 31st. After this date, you'll need to request forms through HR.</p>
      <p>Thank you,<br/>Company Payroll Department</p>
      <small>Payroll Services - Company-HR.net</small>
    </div>`,
    time: '2:30 PM',
    isPhishing: true,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'congruent',
    emailCategory: 'payroll-document',
    threatIndicators: ['domainMismatch', 'personalDataHarvest', 'taxScam'],
    gistCues: ['Tax season urgency', 'HR/Payroll context', 'Personal data request'],
    verbatimCues: ['domainMismatch', 'credentialHarvest', 'unverifiableUrgency'],
    conflictLevel: 'medium',
    complexity: 0.7
  },

  // Additional incongruent phishing emails (migrated from device scenarios)
  {
    id: 'e9',
    domain: 'Dom3', // Internal Finance
    sender: 'John Smith <john.smith@company-mail.net>',
    senderDisplay: 'John Smith',
    senderAddress: 'john.smith@company-mail.net',
    subject: 'Q4 Budget Review - Need Your Input',
    preview: 'Can you review the attached budget spreadsheet before our 2 PM meeting? Let me know if the numbers look right...',
    fullContent: `<div class="email-body">
      <p>Hi,</p>
      <p>Can you review the attached budget spreadsheet before our 2 PM meeting today? I've updated the Q4 projections based on the latest sales data and adjusted the marketing spend to reflect the holiday campaign costs.</p>
      <p>Key changes from last version:</p>
      <ul>
        <li>Marketing budget increased by 15% for Q4 campaign</li>
        <li>IT infrastructure costs adjusted for new cloud migration</li>
        <li>Personnel expenses updated with approved new hires</li>
      </ul>
      <p>Please pay special attention to the personnel line items - I want to make sure those numbers align with what HR shared with you last week. Also, double-check the vendor payment schedule in the second tab.</p>
      <p>Let me know if the numbers look right or if you see anything that needs adjustment before we present to leadership.</p>
      <p>Thanks,<br/>John</p>
    </div>`,
    time: '9:15 AM',
    isPhishing: true,
    hasLink: false,
    hasAttachment: true,
    attachmentName: 'Project_Budget_2024.xlsx',
    unread: true,
    decision: null,
    congruence: 'incongruent',
    emailCategory: 'colleague-work',
    threatIndicators: ['spoofedSender', 'domainSpoofing', 'macroMalware', 'socialEngineering'],
    gistCues: ['Colleague name (Finance dept)', 'Work document type', 'Budget spreadsheet', 'Reasonable meeting request'],
    verbatimCues: ['domainMismatch', 'unverifiableUrgency', 'contextualDetails'],
    conflictLevel: 'high',
    complexity: 0.85
  },

  {
    id: 'e10',
    domain: 'Dom1', // IT Security & System Alerts
    sender: 'IT Security <security-alerts@company-support.net>',
    senderDisplay: 'IT Security',
    senderAddress: 'security-alerts@company-support.net',
    subject: '[URGENT] Password Reset Required - Action Needed',
    preview: 'Your password expires in 24 hours. Scan the QR code with your mobile device to reset password and maintain access...',
    fullContent: `<div class="email-body">
      <p><strong>🔐 SECURITY ALERT</strong></p>
      <p>Your password expires in 24 hours. To maintain access to company systems, you must reset your password immediately.</p>
      <p>For your security and convenience, scan the QR code below with your mobile device to complete the reset process.</p>
      <p>[QR Code Image]</p>
      <p><small>Scan to access: secure-password-reset-portal.net</small></p>
      <p><strong>⚠️ If not completed within 24 hours, your account will be locked and you'll need to contact IT in person.</strong></p>
      <p>Alternatively, <span class="email-link" data-url="https://secure-password-reset-portal.net/reset">click here to reset your password</span> from your desktop.</p>
      <p>Questions? Call IT Support: (555) 0178</p>
      <p>IT Security Team<br/>
      <small>company-support.net</small></p>
    </div>`,
    time: '8:47 AM',
    isPhishing: true,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'incongruent',
    emailCategory: 'security-alert',
    threatIndicators: ['qrPhishing', 'domainSpoofing', 'urgencyPressure', 'credentialHarvest'],
    gistCues: ['Professional IT design', 'Security branding', 'QR code looks modern', 'Urgent 24hr deadline'],
    verbatimCues: ['domainMismatch', 'lookalikeDomain', 'credentialHarvest', 'unverifiableUrgency'],
    conflictLevel: 'high',
    complexity: 0.95
  },

  // ==========================================
  // DOM2: COLLEAGUE COLLABORATION (3 more needed)
  // ==========================================

  // Dom2 - Congruent-Risky
  {
    id: 'e11',
    domain: 'Dom2',
    sender: 'Michael Chen <mchen-temp@gmail.com>',
    senderDisplay: 'Michael Chen',
    senderAddress: 'mchen-temp@gmail.com',
    subject: 'URGENT: Project Deadline Moved Up',
    preview: 'Our client moved the deadline to tomorrow! Need you to run the attached script to fix the data export ASAP...',
    fullContent: `<div class="email-body">
      <p>Hey,</p>
      <p>Bad news - the client just moved our deadline to tomorrow morning. The data export is breaking and we need to fix it NOW.</p>
      <p>I'm working from home today and can't access the company VPN. Can you download and run this script on the production database? It's a quick fix that patches the export function.</p>
      <p>Instructions:</p>
      <ul>
        <li>Download the attached file: data-export-fix.bat</li>
        <li>Right-click and "Run as Administrator"</li>
        <li>It will take 2-3 minutes to complete</li>
        <li>Send me a screenshot when done</li>
      </ul>
      <p>I know this is last minute but we'll miss the deadline otherwise. The client is already upset about the delays.</p>
      <p>Thanks!<br/>Michael</p>
      <small>Sent from my iPhone</small>
    </div>`,
    time: '4:45 PM',
    isPhishing: true,
    hasLink: false,
    hasAttachment: true,
    attachmentName: 'data-export-fix.bat',
    unread: true,
    decision: null,
    congruence: 'congruent',
    emailCategory: 'colleague-work',
    threatIndicators: ['personalEmail', 'executableAttachment', 'urgencyPressure', 'suspiciousScript'],
    gistCues: ['Urgent deadline', 'Personal email (not company)', 'Executable file (.bat)', 'Run as admin request'],
    verbatimCues: ['domainMismatch', 'unverifiableUrgency', 'obviousFakeDomain'],
    conflictLevel: 'low',
    complexity: 0.4
  },

  // Dom2 - Incongruent-Safe
  {
    id: 'e12',
    domain: 'Dom2',
    sender: 'dropbox-notifications@dropbox.com',
    senderDisplay: 'Dropbox',
    senderAddress: 'dropbox-notifications@dropbox.com',
    subject: 'Sarah Johnson shared "Q1 Marketing Plan" with you',
    preview: 'Sarah Johnson (sjohnson@company.com) invited you to collaborate on a Dropbox folder...',
    fullContent: `<div class="email-body">
      <p><strong>Sarah Johnson shared a folder with you</strong></p>
      <p>Sarah Johnson (sjohnson@company.com) invited you to collaborate on:</p>
      <p><strong>"Q1 Marketing Plan - CONFIDENTIAL"</strong></p>
      <p>This folder contains 15 files (247 MB)</p>
      <p><span class="email-link" data-url="https://www.dropbox.com/sh/abc123/Q1-Marketing">View folder in Dropbox</span></p>
      <p>Note from Sarah: "Need your feedback on the new campaign strategy by EOD Thursday. Let me know if you have any issues accessing the files."</p>
      <p>Don't have Dropbox? <span class="email-link" data-url="https://www.dropbox.com/install">Get the app</span></p>
      <small>This email was sent by Dropbox, Inc. 1800 Owens Street, San Francisco, CA 94158</small>
    </div>`,
    time: '11:20 AM',
    isPhishing: false,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'incongruent',
    emailCategory: 'colleague-work',
    legitimacyIndicators: ['legitimateService', 'dropboxDomain', 'properSharing'],
    gistCues: ['External service (Dropbox)', 'Third-party notification', 'Not from company domain'],
    verbatimCues: ['senderDomainMatchesClaimedOrganization', 'linkDomainMatchesClaimedService', 'contextualDetails'],
    conflictLevel: 'high',
    complexity: 0.75
  },

  // Dom2 - Incongruent-Risky
  {
    id: 'e13',
    domain: 'Dom2',
    sender: 'Jennifer Wu <jennifer.wu@company.com>',
    senderDisplay: 'Jennifer Wu',
    senderAddress: 'jennifer.wu@company.com',
    subject: 'Updated Team Roster - Please Review',
    preview: 'Hi team, please review the updated org chart and team assignments. Click to view the interactive version...',
    fullContent: `<div class="email-body">
      <p>Hi team,</p>
      <p>Following our restructuring announcement last week, I've updated the team roster and reporting structure. Please review to ensure all information is accurate.</p>
      <p>The new org chart includes:</p>
      <ul>
        <li>Updated reporting lines for Q1 2025</li>
        <li>New team assignments and project allocations</li>
        <li>Contact information for all team members</li>
        <li>Office locations and seating arrangements</li>
      </ul>
      <p>I've created an interactive version for easier navigation:</p>
      <p><span class="email-link" data-url="https://orgchart.company-hr.net/view/2025-q1">View Interactive Org Chart</span></p>
      <p>(Note: You'll need to log in with your company credentials to access)</p>
      <p>Please review by end of week and let me know if you spot any errors or have questions about your new assignments.</p>
      <p>Best,<br/>Jennifer Wu<br/>HR Operations Manager</p>
    </div>`,
    time: '10:05 AM',
    isPhishing: true,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'incongruent',
    emailCategory: 'colleague-work',
    threatIndicators: ['credentialHarvest', 'domainSpoofing', 'professionalTone', 'hrImpersonation'],
    gistCues: ['HR manager', 'Company domain sender', 'Professional content', 'Org chart context'],
    verbatimCues: ['domainMismatch', 'credentialHarvest', 'lookalikeDomain'],
    conflictLevel: 'high',
    complexity: 0.9
  },

  // ==========================================
  // DOM3: INTERNAL FINANCE (3 more needed)
  // ==========================================

  // Dom3 - Congruent-Safe
  {
    id: 'e14',
    domain: 'Dom3',
    sender: 'Finance <finance@company.com>',
    senderDisplay: 'Finance',
    senderAddress: 'finance@company.com',
    subject: 'Monthly Expense Report - January 2025',
    preview: 'Your January expense report is ready for review. Please approve or submit corrections by February 5th...',
    fullContent: `<div class="email-body">
      <p>Dear Employee,</p>
      <p>Your monthly expense report for January 2025 is now available for review.</p>
      <p><strong>Summary:</strong></p>
      <ul>
        <li>Total Expenses: $1,247.82</li>
        <li>Pending Reimbursement: $892.50</li>
        <li>Corporate Card Charges: $355.32</li>
      </ul>
      <p>Please review your report and take action:</p>
      <p><span class="email-link" data-url="https://expenses.company.com/reports/jan2025">Review Expense Report</span></p>
      <p>If all charges are correct, approve the report by February 5th. If you need to make corrections or add missing receipts, please do so before the deadline.</p>
      <p>Questions? Contact Finance at finance@company.com or ext. 4500</p>
      <p>Finance Department<br/>Company Inc.</p>
    </div>`,
    time: '9:00 AM',
    isPhishing: false,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'congruent',
    emailCategory: 'financial-transaction',
    legitimacyIndicators: ['internalDomain', 'financialProcess', 'properProcedure'],
    gistCues: ['Finance department', 'Company domain', 'Standard expense process'],
    verbatimCues: ['exactOrgDomain', 'firstPartyLinks', 'validContactInfo'],
    conflictLevel: 'low',
    complexity: 0.25
  },

  // Dom3 - Congruent-Risky
  {
    id: 'e15',
    domain: 'Dom3',
    sender: 'finance-alerts@company-finance.org',
    senderDisplay: 'Finance Alerts',
    senderAddress: 'finance-alerts@company-finance.org',
    subject: 'ALERT: Unauthorized Budget Transfer Detected',
    preview: 'Suspicious activity on your department budget. Verify this transaction or your account will be frozen...',
    fullContent: `<div class="email-body">
      <p><strong>⚠️ URGENT FINANCIAL ALERT</strong></p>
      <p>Our automated fraud detection system flagged a suspicious budget transfer from your department account:</p>
      <p><strong>Transaction Details:</strong></p>
      <ul>
        <li>Amount: $45,000.00</li>
        <li>Destination: External Account #8472-9301</li>
        <li>Timestamp: 3:42 AM EST</li>
        <li>Location: IP address from Eastern Europe</li>
      </ul>
      <p><strong>If you did NOT authorize this transfer, verify immediately to prevent financial loss.</strong></p>
      <p><span class="email-link" data-url="https://verify-budget-transfer.company-finance.org/auth">Verify Transaction Status</span></p>
      <p>Failure to verify within 2 hours will result in automatic budget freeze and investigation.</p>
      <p>For immediate assistance: (555) 0199 (24/7 Fraud Hotline)</p>
      <p>Company Finance Security Team</p>
    </div>`,
    time: '7:15 AM',
    isPhishing: true,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'congruent',
    emailCategory: 'financial-transaction',
    threatIndicators: ['domainMismatch', 'urgencyManipulation', 'fraudAlert', 'credentialHarvest'],
    gistCues: ['Fraud alert', 'Suspicious activity', 'External domain', 'Budget freeze threat'],
    verbatimCues: ['domainMismatch', 'unverifiableUrgency', 'credentialHarvest'],
    conflictLevel: 'low',
    complexity: 0.5
  },

  // Dom3 - Incongruent-Safe
  {
    id: 'e16',
    domain: 'Dom3',
    sender: 'quickbooks-notifications@intuit.com',
    senderDisplay: 'QuickBooks',
    senderAddress: 'quickbooks-notifications@intuit.com',
    subject: 'ACTION REQUIRED: Approve Budget Adjustment',
    preview: 'Your manager submitted a budget reallocation request. Approval needed by EOD to process payroll...',
    fullContent: `<div class="email-body">
      <p><strong>Budget Approval Required</strong></p>
      <p>Your manager (David Park, dpark@company.com) submitted a budget adjustment request in QuickBooks:</p>
      <p><strong>Request Details:</strong></p>
      <ul>
        <li>From: Marketing Budget (Account #4520)</li>
        <li>To: Operations Budget (Account #4580)</li>
        <li>Amount: $12,500.00</li>
        <li>Reason: Q1 headcount adjustment for new hires</li>
        <li>Deadline: Today 5:00 PM (required for payroll processing)</li>
      </ul>
      <p><strong>⚠️ This request expires in 6 hours. If not approved, payroll for new hires will be delayed.</strong></p>
      <p><span class="email-link" data-url="https://accounts.intuit.com/app/quickbooks/budgets/approve/BR-2025-0142">Approve Budget Request</span></p>
      <p>Questions? Contact your manager or call QuickBooks Support: 1-800-INTUIT-1</p>
      <small>This email was sent by Intuit QuickBooks on behalf of your organization.</small>
    </div>`,
    time: '11:45 AM',
    isPhishing: false,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'incongruent',
    emailCategory: 'financial-transaction',
    legitimacyIndicators: ['legitimateService', 'intuitDomain', 'companyUses QuickBooks'],
    gistCues: ['External service (QuickBooks)', 'Urgent deadline', 'Payroll warning', 'Third-party domain'],
    verbatimCues: ['linkDomainMatchesClaimedService', 'validContactInfo', 'contextualDetails'],
    conflictLevel: 'high',
    complexity: 0.8
  },

  // ==========================================
  // DOM4: EXECUTIVE/LEADERSHIP (3 more needed)
  // ==========================================

  // Dom4 - Congruent-Safe
  {
    id: 'e17',
    domain: 'Dom4',
    sender: 'Executive Team <exec-team@company.com>',
    senderDisplay: 'Executive Team',
    senderAddress: 'exec-team@company.com',
    subject: 'Q1 All-Hands Meeting - January 30th',
    preview: 'Join us for the quarterly all-hands meeting where leadership will share company updates and Q1 goals...',
    fullContent: `<div class="email-body">
      <p>Dear Team,</p>
      <p>You're invited to our Q1 2025 All-Hands Meeting:</p>
      <p><strong>Meeting Details:</strong></p>
      <ul>
        <li>Date: Thursday, January 30th, 2025</li>
        <li>Time: 10:00 AM - 11:30 AM EST</li>
        <li>Location: Main Conference Hall (or join via Zoom)</li>
        <li>Meeting Link: <span class="email-link" data-url="https://intranet.company.com/events/q1-all-hands">Join All-Hands Meeting</span></li>
      </ul>
      <p>Agenda:</p>
      <ul>
        <li>CEO welcome and 2024 year in review</li>
        <li>Q1 2025 strategic priorities</li>
        <li>Department updates from leadership</li>
        <li>Q&A session</li>
      </ul>
      <p>Please mark your calendars. Attendance is strongly encouraged.</p>
      <p>Looking forward to seeing everyone there!</p>
      <p>Executive Team</p>
    </div>`,
    time: 'Yesterday',
    isPhishing: false,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'congruent',
    emailCategory: 'authority-request',
    legitimacyIndicators: ['internalDomain', 'executiveComm', 'standardMeeting'],
    gistCues: ['Executive team', 'Company domain', 'Standard all-hands format'],
    verbatimCues: ['exactOrgDomain', 'firstPartyLinks', 'specificTimestamps'],
    conflictLevel: 'low',
    complexity: 0.2
  },

  // Dom4 - Congruent-Risky
  {
    id: 'e18',
    domain: 'Dom4',
    sender: 'CEO-Office@company-exec.net',
    senderDisplay: 'CEO Office',
    senderAddress: 'CEO-Office@company-exec.net',
    subject: 'URGENT: Employee Appreciation Gift Cards',
    preview: 'CEO wants to surprise the team with gift cards for meeting Q4 goals. Need you to purchase 50x $100 cards ASAP...',
    fullContent: `<div class="email-body">
      <p>Hello,</p>
      <p>James (CEO) wants to do something special to recognize the team for exceeding Q4 targets. He'd like to distribute surprise gift cards at tomorrow's team meeting.</p>
      <p>Can you help with this urgent request?</p>
      <p><strong>What we need:</strong></p>
      <ul>
        <li>50 Amazon gift cards at $100 each ($5,000 total)</li>
        <li>Purchase them today so we can distribute tomorrow</li>
        <li>Email the gift card codes to this address</li>
        <li>Submit receipt for reimbursement to finance</li>
      </ul>
      <p>I know this is last minute, but James just approved the budget this morning and wants to surprise everyone tomorrow.</p>
      <p>Please confirm you can handle this today. It would mean a lot to the team!</p>
      <p>Thanks,<br/>Rachel Martinez<br/>Executive Assistant to CEO</p>
      <p><small>Sent from mobile - apologies for brevity</small></p>
    </div>`,
    time: '2:20 PM',
    isPhishing: true,
    hasLink: false,
    unread: true,
    decision: null,
    congruence: 'congruent',
    emailCategory: 'authority-request',
    threatIndicators: ['giftCardScam', 'domainMismatch', 'urgencyPressure', 'executiveImpersonation'],
    gistCues: ['CEO office', 'Gift card request', 'External domain', 'Urgent timeline'],
    verbatimCues: ['domainMismatch', 'unverifiableUrgency', 'obviousFakeDomain'],
    conflictLevel: 'low',
    complexity: 0.4
  },

  // Dom4 - Incongruent-Safe
  {
    id: 'e19',
    domain: 'Dom4',
    sender: 'james.wilson@gmail.com',
    senderDisplay: 'James Wilson',
    senderAddress: 'james.wilson@gmail.com',
    subject: 'URGENT: Server Access Needed - On Vacation',
    preview: 'I\'m in Hawaii with spotty WiFi. Need you to approve emergency server access for DevOps ASAP...',
    fullContent: `<div class="email-body">
      <p>Hi,</p>
      <p>I'm on vacation in Hawaii and the hotel WiFi is terrible - can't access our VPN or company systems.</p>
      <p>DevOps just called me about a critical production issue. They need emergency access to the payment processing servers to fix a bug that's blocking customer transactions.</p>
      <p><strong>What I need you to do:</strong></p>
      <ul>
        <li>Log into admin portal: <span class="email-link" data-url="https://admin.company.com">admin.company.com</span></li>
        <li>Go to "Emergency Access Requests"</li>
        <li>Approve request #EA-2025-0089 from DevOps team</li>
        <li>Send me a screenshot confirmation</li>
      </ul>
      <p>This is blocking real revenue - customers can't complete purchases. Time-sensitive.</p>
      <p>I'll explain the full context when I'm back Monday, but we need to act now.</p>
      <p>Thanks for handling this!<br/>James Wilson, CTO</p>
      <p><small>Sent from iPhone - on vacation, limited access</small></p>
    </div>`,
    time: '6:45 PM',
    isPhishing: false,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'incongruent',
    emailCategory: 'authority-request',
    legitimacyIndicators: ['legitimateCTO', 'companyPortal', 'emergencyProcedure'],
    gistCues: ['Personal email (Gmail)', 'CTO on vacation', 'Urgent request', 'Non-company domain'],
    verbatimCues: ['firstPartyLinks', 'contextualDetails', 'validContactInfo'],
    conflictLevel: 'high',
    complexity: 0.85
  },

  // ==========================================
  // DOM5: HR & PAYROLL (3 more needed)
  // ==========================================

  // Dom5 - Congruent-Safe
  {
    id: 'e20',
    domain: 'Dom5',
    sender: 'HR Benefits <benefits@company.com>',
    senderDisplay: 'HR Benefits',
    senderAddress: 'benefits@company.com',
    subject: 'Open Enrollment Period - Choose Your 2025 Benefits',
    preview: 'Annual benefits enrollment is now open. Please review your options and make your selections by February 15th...',
    fullContent: `<div class="email-body">
      <p>Dear Employee,</p>
      <p>Our annual benefits open enrollment period is now active. Please review and select your benefits for 2025.</p>
      <p><strong>Enrollment Period:</strong> February 1-15, 2025</p>
      <p><strong>Available Benefits:</strong></p>
      <ul>
        <li>Medical, dental, and vision insurance</li>
        <li>401(k) contribution adjustments</li>
        <li>Flexible Spending Accounts (FSA)</li>
        <li>Life and disability insurance</li>
        <li>Commuter benefits</li>
      </ul>
      <p>To review options and enroll:</p>
      <p><span class="email-link" data-url="https://benefits.company.com/enrollment">Access Benefits Portal</span></p>
      <p>Important: If you don't make selections by February 15th, your current elections will carry over to 2025.</p>
      <p>Questions? Schedule a 1-on-1 with HR or call benefits hotline: ext. 5000</p>
      <p>Best regards,<br/>HR Benefits Team</p>
    </div>`,
    time: '8:00 AM',
    isPhishing: false,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'congruent',
    emailCategory: 'payroll-document',
    legitimacyIndicators: ['internalDomain', 'hrProcess', 'annualEnrollment'],
    gistCues: ['HR department', 'Company domain', 'Annual enrollment process'],
    verbatimCues: ['exactOrgDomain', 'firstPartySubdomain', 'validContactInfo'],
    conflictLevel: 'low',
    complexity: 0.2
  },

  // Dom5 - Incongruent-Safe
  {
    id: 'e21',
    domain: 'Dom5',
    sender: 'adp-notifications@adp.com',
    senderDisplay: 'ADP Payroll',
    senderAddress: 'adp-notifications@adp.com',
    subject: 'IMMEDIATE ACTION: Verify Direct Deposit Information',
    preview: 'Banking information verification required. Failure to confirm will delay Friday payroll deposit...',
    fullContent: `<div class="email-body">
      <p><strong>⚠️ ACTION REQUIRED: Direct Deposit Verification</strong></p>
      <p>Our records show your direct deposit banking information has not been verified in the past 12 months.</p>
      <p>Federal banking regulations require annual verification of payroll deposit accounts.</p>
      <p><strong>What you need to do:</strong></p>
      <ul>
        <li>Log into ADP portal with your credentials</li>
        <li>Navigate to "Payroll Settings" → "Direct Deposit"</li>
        <li>Click "Verify Banking Information"</li>
        <li>Confirm your routing and account numbers</li>
      </ul>
      <p><span class="email-link" data-url="https://workforcenow.adp.com/verify">Verify Now</span></p>
      <p><strong>DEADLINE: Thursday 11:59 PM EST</strong></p>
      <p>If not completed by deadline, Friday's payroll will be issued as a paper check and mailed to your address on file.</p>
      <p>Questions? Contact ADP Support: 1-800-225-5237</p>
      <small>This email sent by ADP, LLC on behalf of your employer per payroll regulations.</small>
    </div>`,
    time: '1:30 PM',
    isPhishing: false,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'incongruent',
    emailCategory: 'payroll-document',
    legitimacyIndicators: ['legitimateADP', 'adpDomain', 'companyUsesADP'],
    gistCues: ['Warning symbols', 'Banking verification', 'Payroll delay threat', 'Third-party domain (ADP)'],
    verbatimCues: ['linkDomainMatchesClaimedService', 'validContactInfo', 'policyReference'],
    conflictLevel: 'high',
    complexity: 0.85
  },

  // Dom5 - Incongruent-Risky
  {
    id: 'e22',
    domain: 'Dom5',
    sender: 'HR Payroll <hr.payroll@company.com>',
    senderDisplay: 'HR Payroll',
    senderAddress: 'hr.payroll@company.com',
    subject: 'Congratulations! Annual Bonus Notification',
    preview: 'You\'ve been selected for a performance bonus! Review details and confirm acceptance to receive payment...',
    fullContent: `<div class="email-body">
      <p>Dear Employee,</p>
      <p>Congratulations! Based on your outstanding performance in 2024, you have been selected to receive an annual performance bonus.</p>
      <p><strong>Your Bonus Details:</strong></p>
      <ul>
        <li>Performance Rating: Exceeds Expectations</li>
        <li>Bonus Amount: $8,500.00 (gross)</li>
        <li>After-tax estimate: $6,200.00</li>
        <li>Payment Date: Next payroll cycle (February 7th)</li>
      </ul>
      <p>To process your bonus payment, we need you to confirm acceptance and verify your direct deposit information:</p>
      <p><span class="email-link" data-url="https://bonus-confirmation.company-hr.net/accept">Confirm Bonus Acceptance</span></p>
      <p>Please complete this by end of business Friday. Unclaimed bonuses will be forfeited per company policy.</p>
      <p>We appreciate your hard work and dedication to the team!</p>
      <p>Sincerely,<br/>HR Payroll Department</p>
    </div>`,
    time: '3:50 PM',
    isPhishing: true,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'incongruent',
    emailCategory: 'payroll-document',
    threatIndicators: ['credentialHarvest', 'bonusScam', 'domainMismatch', 'financialLure'],
    gistCues: ['HR sender', 'Company domain', 'Bonus notification', 'Professional tone'],
    verbatimCues: ['domainMismatch', 'credentialHarvest', 'lookalikeDomain'],
    conflictLevel: 'high',
    complexity: 0.9
  },

  // ==========================================
  // DOM6: TRAINING & COMPLIANCE (3 more needed)
  // ==========================================

  // Dom6 - Congruent-Safe
  {
    id: 'e23',
    domain: 'Dom6',
    sender: 'Compliance <compliance@company.com>',
    senderDisplay: 'Compliance',
    senderAddress: 'compliance@company.com',
    subject: 'Q1 2025 Ethics & Compliance Training',
    preview: 'Annual ethics training now available. All employees must complete by March 1st to maintain compliance certification...',
    fullContent: `<div class="email-body">
      <p>Dear Team Member,</p>
      <p>Our Q1 2025 Ethics & Compliance training is now available in the learning portal.</p>
      <p><strong>Training Details:</strong></p>
      <ul>
        <li>Course: Annual Ethics & Code of Conduct</li>
        <li>Duration: Approximately 45 minutes</li>
        <li>Deadline: March 1, 2025</li>
        <li>Completion required for all employees</li>
      </ul>
      <p>Topics covered:</p>
      <ul>
        <li>Code of conduct and ethical decision making</li>
        <li>Conflict of interest policies</li>
        <li>Anti-discrimination and harassment prevention</li>
        <li>Data privacy and confidentiality</li>
      </ul>
      <p>Access the training here: <span class="email-link" data-url="https://training.company.com/ethics2025">Begin Training</span></p>
      <p>Note: Completion is tracked automatically. You'll receive a certificate upon finishing.</p>
      <p>Questions? Contact compliance@company.com or ext. 6000</p>
      <p>Compliance Department</p>
    </div>`,
    time: '9:30 AM',
    isPhishing: false,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'congruent',
    emailCategory: 'policy-compliance',
    legitimacyIndicators: ['internalDomain', 'complianceTraining', 'standardProcedure'],
    gistCues: ['Compliance department', 'Company domain', 'Standard annual training'],
    verbatimCues: ['exactOrgDomain', 'firstPartySubdomain', 'validContactInfo'],
    conflictLevel: 'low',
    complexity: 0.25
  },

  // Dom6 - Congruent-Risky
  {
    id: 'e24',
    domain: 'Dom6',
    sender: 'corporate-training@company-learn.org',
    senderDisplay: 'Corporate Training',
    senderAddress: 'corporate-training@company-learn.org',
    subject: 'MANDATORY: Workplace Safety Certification Expires Today',
    preview: 'Your safety certification expires at midnight. Immediate renewal required to avoid compliance violation and potential suspension...',
    fullContent: `<div class="email-body">
      <p><strong>⚠️ URGENT COMPLIANCE ALERT</strong></p>
      <p>Our records indicate your Workplace Safety Certification expires today at 11:59 PM.</p>
      <p><strong>OSHA regulations require all employees maintain current safety certification.</strong></p>
      <p>Failure to renew will result in:</p>
      <ul>
        <li>Immediate suspension from workplace access</li>
        <li>Compliance violation on your employee record</li>
        <li>Potential disciplinary action</li>
        <li>Required in-person make-up training (8 hours)</li>
      </ul>
      <p>Renew your certification now (takes 15 minutes):</p>
      <p><span class="email-link" data-url="https://safety-cert-renewal.company-learn.org/renew">RENEW CERTIFICATION NOW</span></p>
      <p><strong>You must complete this before midnight to avoid penalties.</strong></p>
      <p>For technical support: (555) 0166</p>
      <p>Corporate Training Department</p>
    </div>`,
    time: '6:30 PM',
    isPhishing: true,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'congruent',
    emailCategory: 'policy-compliance',
    threatIndicators: ['domainMismatch', 'urgencyManipulation', 'credentialHarvest', 'complianceScam'],
    gistCues: ['Urgent compliance deadline', 'Suspension threat', 'External domain', 'OSHA reference'],
    verbatimCues: ['domainMismatch', 'unverifiableUrgency', 'credentialHarvest'],
    conflictLevel: 'low',
    complexity: 0.5
  },

  // Dom6 - Incongruent-Risky
  {
    id: 'e25',
    domain: 'Dom6',
    sender: 'Learning & Development <learning@company.com>',
    senderDisplay: 'Learning & Development',
    senderAddress: 'learning@company.com',
    subject: 'New Course Available: Advanced Leadership Skills',
    preview: 'You\'ve been nominated for our executive leadership development program. Enroll now to secure your spot...',
    fullContent: `<div class="email-body">
      <p>Congratulations!</p>
      <p>Your manager has nominated you for our Advanced Leadership Skills development program, a prestigious initiative reserved for high-potential employees.</p>
      <p><strong>Program Highlights:</strong></p>
      <ul>
        <li>12-week executive coaching program</li>
        <li>Certificate of completion from Harvard Business School Online</li>
        <li>Networking with senior leadership</li>
        <li>Fast-track consideration for management roles</li>
      </ul>
      <p>This program typically costs $4,500 per person, but the company is covering the full tuition for selected nominees.</p>
      <p><strong>Limited to 20 participants - enrollment closes Friday!</strong></p>
      <p>To secure your spot, complete enrollment and verify your employee information:</p>
      <p><span class="email-link" data-url="https://leadership.company-learn.org/enroll">Enroll in Leadership Program</span></p>
      <p>This is a career-accelerating opportunity - don't miss out!</p>
      <p>Best regards,<br/>Learning & Development Team</p>
    </div>`,
    time: '1:15 PM',
    isPhishing: true,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'incongruent',
    emailCategory: 'policy-compliance',
    threatIndicators: ['credentialHarvest', 'domainMismatch', 'prestige Lure', 'urgencyPressure'],
    gistCues: ['L&D sender', 'Company domain', 'Professional opportunity', 'Manager nomination'],
    verbatimCues: ['domainMismatch', 'credentialHarvest', 'lookalikeDomain'],
    conflictLevel: 'high',
    complexity: 0.88
  },

  // ==========================================
  // DOM7: CUSTOMER/CLIENT COMMUNICATIONS (4 needed)
  // ==========================================

  // Dom7 - Congruent-Safe
  {
    id: 'e26',
    domain: 'Dom7',
    sender: 'Lisa Parker <lisa.parker@acmecorp.com>',
    senderDisplay: 'Lisa Parker',
    senderAddress: 'lisa.parker@acmecorp.com',
    subject: 'Project Milestone Feedback - Phase 2 Deliverables',
    preview: 'Hi team, great work on Phase 2! A few minor adjustments needed before we proceed to Phase 3...',
    fullContent: `<div class="email-body">
      <p>Hi team,</p>
      <p>I've reviewed the Phase 2 deliverables you submitted last week. Overall, excellent work! The technical implementation is solid and the documentation is thorough.</p>
      <p>A few minor adjustments before we move to Phase 3:</p>
      <ul>
        <li>API integration: Can you add rate limiting to the user endpoint?</li>
        <li>Dashboard: Update the color scheme to match our brand guidelines (attached)</li>
        <li>Documentation: Add troubleshooting section for common errors</li>
      </ul>
      <p>These are small tweaks - shouldn't take more than a few days. Once complete, we're green-lit for Phase 3 kickoff.</p>
      <p>Let's schedule a quick sync next Tuesday to discuss Phase 3 timeline and requirements.</p>
      <p>Available slots: Tuesday 2-4 PM or Wednesday 10 AM - 12 PM. Let me know what works!</p>
      <p>Thanks again for the great work.</p>
      <p>Best,<br/>Lisa Parker<br/>Project Manager, Acme Corp</p>
    </div>`,
    time: '10:45 AM',
    isPhishing: false,
    hasLink: false,
    hasAttachment: true,
    attachmentName: 'Acme_Brand_Guidelines.pdf',
    unread: true,
    decision: null,
    congruence: 'congruent',
    emailCategory: 'colleague-work',
    legitimacyIndicators: ['clientDomain', 'projectContext', 'professionalTone'],
    gistCues: ['Client email', 'Project feedback', 'Acme Corp domain', 'Professional tone'],
    verbatimCues: ['senderDomainMatchesClaimedOrganization', 'contextualDetails', 'specificTimestamps'],
    conflictLevel: 'low',
    complexity: 0.3
  },

  // Dom7 - Congruent-Risky
  {
    id: 'e27',
    domain: 'Dom7',
    sender: 'support@client-services.biz',
    senderDisplay: 'Client Support',
    senderAddress: 'support@client-services.biz',
    subject: 'Account Suspension Notice - Payment Method Failed',
    preview: 'Your client account payment method was declined. Update billing information within 24 hours to avoid service termination...',
    fullContent: `<div class="email-body">
      <p>Dear Account Holder,</p>
      <p>We attempted to process your monthly subscription payment but your credit card was declined.</p>
      <p><strong>Account Status: SUSPENDED</strong></p>
      <p><strong>Reason:</strong> Payment method failed (Card ending in 4829)</p>
      <p><strong>Amount Due:</strong> $299.00</p>
      <p>Your account and all associated services have been temporarily suspended. To restore access:</p>
      <p><span class="email-link" data-url="http://update-billing-info.client-services.biz/pay">Update Payment Method</span></p>
      <p><strong>⚠️ You have 24 hours to update your billing information or your account will be permanently closed and all data deleted.</strong></p>
      <p>If you believe this is an error, contact our billing department immediately: billing@client-services.biz</p>
      <p>Account Services Team<br/>Client Services Inc.</p>
    </div>`,
    time: '5:20 PM',
    isPhishing: true,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'congruent',
    emailCategory: 'financial-transaction',
    threatIndicators: ['suspiciousDomain', 'httpNotHttps', 'paymentScam', 'urgencyManipulation'],
    gistCues: ['Account suspension', 'Payment failure', 'Suspicious domain (.biz)', 'Data deletion threat'],
    verbatimCues: ['obviousFakeDomain', 'httpNotHttps', 'unverifiableUrgency'],
    conflictLevel: 'low',
    complexity: 0.45
  },

  // Dom7 - Incongruent-Safe
  {
    id: 'e28',
    domain: 'Dom7',
    sender: 'david.chen.personal@gmail.com',
    senderDisplay: 'David Chen',
    senderAddress: 'david.chen.personal@gmail.com',
    subject: 'URGENT: Need Contract Signed by EOD',
    preview: 'Hey, my work laptop died and I can\'t access email. Need you to review and sign the MSA amendment ASAP...',
    fullContent: `<div class="email-body">
      <p>Hi,</p>
      <p>My work laptop completely died this morning (won't even turn on) and our IT department can't get me a replacement until tomorrow. Emailing from my personal account.</p>
      <p>We have a major problem: The Master Service Agreement amendment for the Q1 project needs to be signed by both parties TODAY or the project kickoff gets delayed by 2 weeks (legal/procurement rules).</p>
      <p>Can you please:</p>
      <ul>
        <li>Download the MSA amendment from our shared drive: <span class="email-link" data-url="https://sharepoint.company.com/contracts/MSA-Amendment-Q1-2025.pdf">MSA Amendment Link</span></li>
        <li>Review the terms (standard scope expansion, no rate changes)</li>
        <li>Sign via DocuSign and return by 5 PM today</li>
      </ul>
      <p>I know emailing from Gmail looks sketchy, but you can verify this is legit by:</p>
      <ul>
        <li>Call my work phone: (555) 234-5678</li>
        <li>Check the SharePoint link (it's our official company portal)</li>
        <li>The DocuSign will come from DocuSign's official domain</li>
      </ul>
      <p>Sorry for the inconvenience - stupid laptop timing!</p>
      <p>Thanks,<br/>David Chen<br/>VP of Client Services, Acme Corp</p>
    </div>`,
    time: '2:50 PM',
    isPhishing: false,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'incongruent',
    emailCategory: 'colleague-work',
    legitimacyIndicators: ['legitimateClient', 'companySharePoint', 'verificationMethods'],
    gistCues: ['Personal email (Gmail)', 'Urgent contract', 'Laptop died excuse', 'Non-corporate domain'],
    verbatimCues: ['firstPartyLinks', 'validContactInfo', 'contextualDetails'],
    conflictLevel: 'high',
    complexity: 0.82
  },

  // Dom7 - Incongruent-Risky
  {
    id: 'e29',
    domain: 'Dom7',
    sender: 'Robert Martinez <r.martinez@globaltech-inc.com>',
    senderDisplay: 'Robert Martinez',
    senderAddress: 'r.martinez@globaltech-inc.com',
    subject: 'Confidential: Acquisition Opportunity Discussion',
    preview: 'Our CEO wants to discuss a potential acquisition of your company. Extremely confidential - please review NDA and proposal...',
    fullContent: `<div class="email-body">
      <p>Dear Executive Team,</p>
      <p>I'm reaching out on behalf of GlobalTech Inc. regarding a highly confidential business opportunity.</p>
      <p>Our CEO, Michael Stevens, has identified your company as a strategic acquisition target. We believe there's strong synergy between our organizations and would like to explore a potential acquisition.</p>
      <p><strong>Proposed Terms (Preliminary):</strong></p>
      <ul>
        <li>Acquisition Price: $45M - $52M (subject to due diligence)</li>
        <li>Timeline: Close within 90 days</li>
        <li>Retention: Key employees retained with 3-year contracts</li>
        <li>Integration: Operate as independent subsidiary</li>
      </ul>
      <p>Given the sensitive nature, we require all parties to sign an NDA before sharing detailed proposal documents:</p>
      <p><span class="email-link" data-url="https://secure-nda-signing.globaltech-inc.com/sign">Review and Sign NDA</span></p>
      <p><strong>CONFIDENTIAL: Please do not share this communication with anyone outside your executive team.</strong></p>
      <p>Once NDA is executed, we'll schedule a confidential call with our CEO to discuss next steps.</p>
      <p>Time-sensitive - we have other acquisition targets in parallel consideration.</p>
      <p>Best regards,<br/>Robert Martinez<br/>VP of Corporate Development<br/>GlobalTech Inc.</p>
    </div>`,
    time: '4:10 PM',
    isPhishing: true,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'incongruent',
    emailCategory: 'authority-request',
    threatIndicators: ['credentialHarvest', 'domainSpoofing', 'acquisitionScam', 'confidentialityPressure'],
    gistCues: ['Professional corporate communication', 'Legitimate-sounding company', 'Detailed financial terms', 'Executive level'],
    verbatimCues: ['domainMismatch', 'credentialHarvest', 'lookalikeDomain'],
    conflictLevel: 'high',
    complexity: 0.92
  },

  // ==========================================
  // DOM8: EXTERNAL PAYMENTS (3 more needed)
  // ==========================================

  // Dom8 - Congruent-Safe
  {
    id: 'e30',
    domain: 'Dom8',
    sender: 'AWS Billing <billing@amazon.com>',
    senderDisplay: 'AWS Billing',
    senderAddress: 'billing@amazon.com',
    subject: 'Your AWS Invoice for January 2025',
    preview: 'Your monthly AWS bill is ready. Total: $3,247.85. Payment will be charged to card on file on February 5th...',
    fullContent: `<div class="email-body">
      <p>Hello,</p>
      <p>Your AWS invoice for January 2025 is now available.</p>
      <p><strong>Invoice Summary:</strong></p>
      <ul>
        <li>Invoice Number: INV-2025-01-847392</li>
        <li>Billing Period: January 1-31, 2025</li>
        <li>Total Amount Due: $3,247.85</li>
        <li>Payment Date: February 5, 2025</li>
        <li>Payment Method: Visa ending in 8472</li>
      </ul>
      <p>Service breakdown:</p>
      <ul>
        <li>EC2 Instances: $1,842.30</li>
        <li>S3 Storage: $687.45</li>
        <li>RDS Database: $512.80</li>
        <li>CloudFront CDN: $205.30</li>
      </ul>
      <p>View detailed invoice: <span class="email-link" data-url="https://console.aws.amazon.com/billing/invoices/2025-01">View Invoice</span></p>
      <p>Payment will be automatically charged to your card on file. No action needed unless you want to update payment method.</p>
      <p>Questions? Contact AWS Support or visit our billing FAQ.</p>
      <p>Amazon Web Services</p>
    </div>`,
    time: '6:00 AM',
    isPhishing: false,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'congruent',
    emailCategory: 'financial-transaction',
    legitimacyIndicators: ['amazonDomain', 'awsBilling', 'legitimateService'],
    gistCues: ['AWS billing', 'Amazon domain', 'Standard monthly invoice'],
    verbatimCues: ['senderDomainMatchesClaimedOrganization', 'linkDomainMatchesClaimedService', 'specificTimestamps'],
    conflictLevel: 'low',
    complexity: 0.2
  },

  // Dom8 - Congruent-Risky
  {
    id: 'e31',
    domain: 'Dom8',
    sender: 'vendor-services@supplier-portal.biz',
    senderDisplay: 'Vendor Services',
    senderAddress: 'vendor-services@supplier-portal.biz',
    subject: 'URGENT: Vendor Contract Renewal - Payment Required',
    preview: 'Your annual software license expires in 48 hours. Renew now to avoid service interruption and data loss...',
    fullContent: `<div class="email-body">
      <p><strong>⚠️ LICENSE EXPIRATION WARNING</strong></p>
      <p>Your annual software license agreement expires in 48 hours:</p>
      <p><strong>License Details:</strong></p>
      <ul>
        <li>Software: Enterprise Cloud Backup Suite</li>
        <li>License ID: ECB-2024-9472</li>
        <li>Expiration: February 3, 2025 at 11:59 PM</li>
        <li>Renewal Cost: $12,500/year</li>
      </ul>
      <p><strong>⚠️ CRITICAL: If not renewed within 48 hours:</strong></p>
      <ul>
        <li>All backup services will be TERMINATED</li>
        <li>Your 847 GB of stored backups will be DELETED</li>
        <li>No grace period - data permanently lost</li>
        <li>Re-activation fee: $5,000 (if within 30 days)</li>
      </ul>
      <p>Renew your license immediately to prevent data loss:</p>
      <p><span class="email-link" data-url="http://license-renewal.supplier-portal.biz/renew">RENEW LICENSE NOW</span></p>
      <p>Process payment via credit card or wire transfer (instructions on renewal page).</p>
      <p>Vendor Support: (555) 0188<br/>Reference: ECB-2024-9472</p>
      <p>Vendor Services Department</p>
    </div>`,
    time: '7:45 AM',
    isPhishing: true,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'congruent',
    emailCategory: 'financial-transaction',
    threatIndicators: ['suspiciousDomain', 'httpNotHttps', 'urgencyManipulation', 'dataLossThreat'],
    gistCues: ['License expiration', 'Data deletion threat', 'Suspicious domain (.biz)', 'Urgent payment request'],
    verbatimCues: ['obviousFakeDomain', 'httpNotHttps', 'unverifiableUrgency'],
    conflictLevel: 'low',
    complexity: 0.5
  },

  // Dom8 - Incongruent-Safe
  {
    id: 'e32',
    domain: 'Dom8',
    sender: 'invoices@bill.com',
    senderDisplay: 'Bill.com',
    senderAddress: 'invoices@bill.com',
    subject: 'Payment Approved - Action Required to Process',
    preview: 'Your manager approved invoice #INV-8472. Verify payment details to release funds to vendor...',
    fullContent: `<div class="email-body">
      <p><strong>Payment Approval Notification</strong></p>
      <p>Your manager (Sarah Johnson, sjohnson@company.com) approved a vendor payment in Bill.com:</p>
      <p><strong>Invoice Details:</strong></p>
      <ul>
        <li>Vendor: TechSupply Solutions LLC</li>
        <li>Invoice: INV-8472</li>
        <li>Amount: $18,450.00</li>
        <li>Description: Q1 Hardware Procurement</li>
        <li>Approved by: Sarah Johnson (Manager)</li>
        <li>Approval Date: February 1, 2025</li>
      </ul>
      <p><strong>⚠️ FINAL STEP REQUIRED:</strong> As the designated account administrator, you must verify payment details before funds are released.</p>
      <p>Please log in to Bill.com and:</p>
      <ol>
        <li>Review vendor banking information</li>
        <li>Confirm invoice matches PO #4829-Q1</li>
        <li>Authorize payment release</li>
      </ol>
      <p><span class="email-link" data-url="https://app.bill.com/payments/review/INV-8472">Review and Authorize Payment</span></p>
      <p><strong>Payment will be processed within 24 hours of your authorization.</strong></p>
      <p>Questions? Contact Bill.com Support: 1-800-BILL-PAY</p>
      <small>This email sent by Bill.com on behalf of your organization's payment workflow.</small>
    </div>`,
    time: '3:35 PM',
    isPhishing: false,
    hasLink: true,
    unread: true,
    decision: null,
    congruence: 'incongruent',
    emailCategory: 'financial-transaction',
    legitimacyIndicators: ['legitimateBillCom', 'billComDomain', 'companyUsesBillCom'],
    gistCues: ['Third-party payment platform', 'Urgent payment authorization', 'External service domain'],
    verbatimCues: ['linkDomainMatchesClaimedService', 'validContactInfo', 'contextualDetails'],
    conflictLevel: 'high',
    complexity: 0.85
  },

];

// Derive display and address if missing. Robust to "Name <addr>" and bare addresses.
export function normalizeScenarios(list: EmailScenario[]): EmailScenario[] {
  return list.map(s => {
    let senderDisplay = s.senderDisplay;
    let senderAddress = s.senderAddress;

    if (!senderDisplay || !senderAddress) {
      const m = s.sender.match(/^\s*(.*?)\s*<\s*([^>]+)\s*>\s*$/);
      if (m) {
        senderDisplay = senderDisplay || m[1];
        senderAddress = senderAddress || m[2];
      } else {
        // Fallback when sender is a bare address
        senderDisplay = senderDisplay || s.sender.split('@')[0];
        senderAddress = senderAddress || s.sender.trim();
      }
    }
    return { ...s, senderDisplay, senderAddress };
  });
}

// Helper: Seeded shuffle using Fisher-Yates algorithm
function seededShuffle<T>(array: T[], seed: number): T[] {
  const arr = [...array]; // Copy to avoid mutation
  let currentSeed = seed;

  // Mulberry32 PRNG
  const random = () => {
    currentSeed |= 0;
    currentSeed = (currentSeed + 0x6d2b79f5) | 0;
    let t = Math.imul(currentSeed ^ (currentSeed >>> 15), currentSeed | 1);
    t ^= t + Math.imul(t ^ (t >>> 7), t | 61);
    return ((t ^ (t >>> 14)) >>> 0) / 4294967296;
  };

  // Fisher-Yates shuffle
  for (let i = arr.length - 1; i > 0; i--) {
    const j = Math.floor(random() * (i + 1));
    [arr[i], arr[j]] = [arr[j], arr[i]];
  }

  return arr;
}

// Function to get balanced scenarios using Latin Square counterbalancing
// NEW APPROACH: Shows 4 scenarios (1 per condition) from 4 domains, with guaranteed cross-participant balance
// GUARANTEES:
// - Each participant sees exactly 1 Congruent-Safe, 1 Congruent-Risky, 1 Incongruent-Safe, 1 Incongruent-Risky
// - Across every 8 participants, each domain appears exactly once with each condition (perfect balance)
export function getScenariosByCongruence(participantId?: string): EmailScenario[] {
  const src = normalizeScenarios(emailScenarios);

  // Create seed from participant ID
  let seedValue = 0;
  if (participantId) {
    for (let i = 0; i < participantId.length; i++) {
      seedValue += participantId.charCodeAt(i);
    }
  } else {
    seedValue = Math.floor(Math.random() * 1000000);
  }

  // Group scenarios by domain
  const domainMap: { [key: string]: EmailScenario[] } = {};
  src.forEach(scenario => {
    if (!domainMap[scenario.domain]) {
      domainMap[scenario.domain] = [];
    }
    domainMap[scenario.domain].push(scenario);
  });

  // All 8 domains
  const allDomains = ['Dom1', 'Dom2', 'Dom3', 'Dom4', 'Dom5', 'Dom6', 'Dom7', 'Dom8'];

  // All 4 conditions
  const allConditions = ['Congruent-Safe', 'Congruent-Risky', 'Incongruent-Safe', 'Incongruent-Risky'];

  // Calculate rotation index from participant ID (0-7)
  // This ensures perfect counterbalancing across every 8 participants
  const rotationIndex = seedValue % 8;

  // Latin Square rotation patterns
  // Each pattern selects 4 domains and assigns one condition to each
  // Pattern 0: Dom1(CS), Dom2(CR), Dom3(IS), Dom4(IR)
  // Pattern 1: Dom2(CS), Dom3(CR), Dom4(IS), Dom5(IR)
  // ...and so on, cycling through domains
  const conditionAssignments = [];
  for (let i = 0; i < 4; i++) {
    const domainIndex = (rotationIndex + i) % 8; // Cycle through domains
    const domain = allDomains[domainIndex];
    const condition = allConditions[i]; // Assign conditions in order: CS, CR, IS, IR

    conditionAssignments.push({
      domains: [domain],
      condition: condition
    });
  }

  // Select scenarios based on Latin Square assignments
  const selected: EmailScenario[] = [];

  conditionAssignments.forEach(assignment => {
    assignment.domains.forEach(domain => {
      const domainScenarios = domainMap[domain];

      // Find scenario matching the assigned condition
      const [congruence, risk] = assignment.condition.split('-');
      const matchingScenario = domainScenarios.find(s => {
        const isCongruent = s.congruence === 'congruent';
        const isSafe = !s.isPhishing;

        const matchesCongruence =
          (congruence === 'Congruent' && isCongruent) ||
          (congruence === 'Incongruent' && !isCongruent);

        const matchesRisk =
          (risk === 'Safe' && isSafe) ||
          (risk === 'Risky' && !isSafe);

        return matchesCongruence && matchesRisk;
      });

      if (matchingScenario) {
        selected.push(matchingScenario);
      } else {
        console.error(`No matching scenario found for ${domain} with condition ${assignment.condition}`);
      }
    });
  });

  // Shuffle the selected scenarios so they're not always in the same order
  const shuffledSelected = seededShuffle(selected, seedValue + 1000);

  // Verify we have exactly 4 scenarios
  if (shuffledSelected.length !== 4) {
    throw new Error(`Expected 4 scenarios, got ${shuffledSelected.length}. Participant: ${participantId}`);
  }

  // Verify distribution
  const counts = {
    'Congruent-Safe': 0,
    'Congruent-Risky': 0,
    'Incongruent-Safe': 0,
    'Incongruent-Risky': 0
  };

  shuffledSelected.forEach(s => {
    const isCongruent = s.congruence === 'congruent';
    const isSafe = !s.isPhishing;
    const key = `${isCongruent ? 'Congruent' : 'Incongruent'}-${isSafe ? 'Safe' : 'Risky'}`;
    counts[key as keyof typeof counts]++;
  });

  // Assert exactly 1 of each condition
  Object.entries(counts).forEach(([condition, count]) => {
    if (count !== 1) {
      throw new Error(`Expected 1 scenario for ${condition}, got ${count}. Participant: ${participantId}`);
    }
  });

  return shuffledSelected;
}

// Function to ensure balanced phishing/legitimate distribution
export function balanceScenarios(scenarios: EmailScenario[]): EmailScenario[] {
  // Already balanced by getScenariosByCongruence, just return as is
  return scenarios;
}
